/*
  Lunya brand adapter: behaviour for snippets/lunya-plp-toolbar.liquid, the source storefront's
  collection toolbar rebuilt on Symmetry's <filter-container>. Symmetry (assets/main.js) still does
  the filtering: it listens for `change` and `submit` on #CollectionFilterForm, ajax-loads the new
  URL and replaces this element's children. This element therefore attaches delegated listeners to
  itself so they survive the swap, and adds only what the source has and Symmetry lacks:

  - one dropdown open at a time, closed by outside click, Escape or CLOSE;
  - the open dropdown reopened after Symmetry re-renders;
  - the mobile FILTER panel, whose ticks are held back until Apply (n) or discarded by Cancel;
  - focus kept on something the shopper can see, across the ajax swap and across a breakpoint.

  It also takes over submission for the deliberate one-shot commits — Apply, the mobile SORT
  <select>, and a desktop tick made while a fetch is live — calling the controller's `ajaxLoadForm`
  itself and stopping the event. That is load-bearing, not a shortcut: Symmetry debounces its own
  handler by 700ms, and in that window any response already in flight replaces the form, after which
  the delayed pass serialises the replacement and the shopper's selection is gone. Calling it
  directly serialises what they acted on and aborts the in-flight request on the way through. The
  desktop debounce is run here for the same reason — Symmetry's is a local inside its
  connectedCallback, so a queued pass cannot be cancelled when a later click supersedes it.

  The mobile SORT <select> is still named sort_by, which is what carries the sort through any form
  serialisation on either breakpoint, since desktop sort is a link rather than an input.
*/
(() => {
  // No custom elements at all: leave the page to the CSS fallback rather than throwing. The probe in
  // sections/main-collection.liquid flags `lunya-plp-unavailable` for exactly this case.
  /*
    `filter-container` as well as the guards against upgrading twice: every listener this element
    drives belongs to Symmetry's controller, so if main.js failed while this asset loaded there is
    nothing to apply a filter with. Changing only the CSS would leave real buttons over
    always-visible panels, each claiming to collapse something. Not upgrading keeps the native
    fallback whole. Both scripts `defer`, so main.js has run by the time this does.
  */
  if (!window.customElements
    || window.customElements.get('lunya-plp-toolbar')
    || !window.customElements.get('filter-container')) return;

  const MOBILE = window.matchMedia('(max-width: 900px)');
  // Ceiling on the Apply lock: Symmetry's 700ms debounce plus a request that may never resolve.
  const APPLY_LOCK_MAX = 10000;
  // Matches the debounce Symmetry wraps its own handler in (assets/main.js, FilterContainer constructor: `debounce(this.ajaxLoadForm…, 700)`).
  const SUBMIT_DEBOUNCE = 700;
  /*
    Long enough for the panel's own 0.3s transition to have flipped `visibility`, which is the last
    thing a breakpoint crossing changes about whether the focused control is still rendered.
  */
  const REHOME_SETTLE = 350;
  /*
    How recently focus must have been inside the toolbar for a crossing to re-home from where the
    shopper was. A resize can blur the focused control before the media listener runs — observed
    with the bar's `<select>` — leaving `document.activeElement` on the document with nothing to
    aim from. Short enough that a deliberate click away is not mistaken for one.
  */
  const HELD_RECALL = 1000;
  const now = () => (window.performance && window.performance.now ? window.performance.now() : Date.now());
  // Verbatim from the delegation Symmetry binds when ajax filtering is on (assets/main.js, FilterContainer constructor, the `ajaxFiltering` link delegate):
  // every one of these replaces this toolbar as soon as its response lands.
  const AJAX_LINKS = '.link-dropdown__link, .filter-group__applied-item, .filter-group__clear-link, .pagination a';
  const OPEN_CLASS = 'lunya-plp-filter-open';

  /*
    Symmetry's breakpoint setup (assets/main.js, `mqLists`) tries `addEventListener` and falls back
    to the legacy `addListener`, which is what keeps Safari < 14 working — `addEventListener` on a
    MediaQueryList is missing there. This element does the same, so it matches the theme's support.
    Symmetry locations below are cited by function and comment, not line number, which drifts.
  */
  /*
    The theme's own focus trap (main.js, also assigned to `window.trapFocus`), used by its drawers
    and modals. Guarded, so a missing helper degrades to no trap rather than throwing.
  */
  /*
    `display: none` drops out of the box tree, so it has no client rects; `visibility: hidden` keeps
    its rects but refuses focus. The panel and the breakpoints use both, so a focus target has to
    clear each test before it is worth focusing.
  */
  const isHidden = (el) => el.getClientRects().length === 0
    || window.getComputedStyle(el).visibility === 'hidden';

  const trapFocusIn = (container, elementToFocus) => {
    if (typeof window.trapFocus === 'function') window.trapFocus(container, elementToFocus);
  };

  const releaseFocusTrap = (elementToFocus) => {
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(elementToFocus || null);
    else if (elementToFocus) elementToFocus.focus();
  };

  const onMediaChange = (mq, handler) => {
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', handler);
    else mq.addListener(handler);
  };

  const offMediaChange = (mq, handler) => {
    if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', handler);
    else mq.removeListener(handler);
  };

  class LunyaPlpToolbar extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.openHandle = null;
      this.snapshot = null;
      this.pendingFocusId = null;
      this.pendingToggleFocus = false;
      this.pendingRehome = null;
      this.lastInsideFocus = null;
      this.applying = false;

      // Bound once and kept, so disconnectedCallback can remove exactly what was added: it clears
      // `wired`, so a disconnect/reconnect (theme-editor section lifecycle, a DOM move) would
      // otherwise add a second pair and every dropdown click would open then immediately close.
      this.boundClick = this.onClick.bind(this);
      this.boundBreakpoint = () => {
        /*
          Crossing above 900px with the panel open (a tablet rotation) would turn pending checkboxes
          into live desktop controls whose state disagrees with the results — and the next desktop
          change would serialise them — while `lunya-plp-filter-open` lingered and reopened, and
          re-locked, the panel on the way back. Discard the pending edits, as Cancel does.
        */
        const mobile = this.isMobile();
        const closingPanel = !mobile && document.documentElement.classList.contains(OPEN_CLASS);
        /*
          The mirror case: crossing down with a desktop dropdown open hides the whole groups
          container, but `is-open` and `openHandle` would survive — so focus vanishes with the
          panel, the next ajax swap reopens a dropdown nobody can see, and crossing back up
          re-reveals the stale one.
        */
        const closingDropdown = mobile && this.openHandle != null;
        /*
          Read before the closes below, which make the focused control `visibility: hidden` and drop
          focus to the document. Where it goes instead is decided after the sync, since a desktop
          trigger is only focusable once the sync strips the tabindex="-1" the static markup ships.
        */
        let held = document.activeElement != null && this.contains(document.activeElement)
          ? document.activeElement
          : null;
        /*
          The resize can blur the focused control before this listener runs, so focus is already on
          the document and there is nothing to aim from. Fall back to where the shopper just was —
          only from the document, so a deliberate click onto something else still wins.
        */
        if (!held && document.activeElement === document.body && this.lastInsideFocus
          && now() - this.lastInsideFocus.at < HELD_RECALL
          && this.contains(this.lastInsideFocus.el)) {
          held = this.lastInsideFocus.el;
        }
        if (closingPanel) {
          this.restoreSnapshot();
          this.closeMobile();
        }
        if (closingDropdown) this.closeDropdowns();
        this.syncTriggerSemantics();
        /*
          React to what the crossing did to focus rather than predicting it. Whether the new width
          hides the held control cannot be read at a single instant: measured in Chromium, an option
          two frames past the crossing still reported one client rect and `visibility: visible`, and
          only lost focus later as the panel's transition flipped — so a hidden test here skipped the
          re-home entirely and left focus on the document. Instead, once things have settled, if the
          browser has dropped focus out of this toolbar (or onto something now hidden), hand it to
          the counterpart the new width actually renders.

          Run on the next frame for the `display: none` case and again after the transition for the
          `visibility` one. The check is idempotent: it returns early once focus sits on a rendered
          control inside this element.
        */
        if (held) {
          /*
            Returns whether focus has come to rest somewhere sensible, so the ladder below can stop.
            No test of the target beforehand: measured here, it reads as unrendered at the moment of
            the crossing just as the held control reads as rendered, and `focus()` on something the
            browser has not laid out yet is a silent no-op. Attempt it, check whether it took, and
            try again once the next stage of the crossing has landed.
          */
          const settle = () => {
            const now = document.activeElement;
            /*
              Focus already resting on something rendered inside this element, and not the control
              the crossing is about to take away: done.
            */
            if (now && now !== held && now !== document.body && this.contains(now) && !isHidden(now)) {
              return true;
            }
            /*
              Still on the held control and still rendered — which is the state at the moment the
              media listener fires, measured here as visible with one client rect for over 300ms
              before the browser gave up on it. Not done, and not a cue to act either: keep waiting.
              Treating this as success is what left focus on the document, because the ladder stopped
              before the drop happened.
            */
            if (now === held && !isHidden(held)) return false;
            const target = this.counterpartFor(held, mobile);
            if (!target) return true;
            // Flush, as the restore does: focus() against stale style is a silent no-op.
            void target.offsetWidth;
            target.focus();
            return document.activeElement === target;
          };
          /*
            A response already in flight replaces this whole toolbar, and it can detach the control
            the ladder just focused — rotating within a second of Apply is the case, and it is why
            the ladder alone left focus on the document intermittently. Hand the observer a one-shot
            with the direction to re-home in, but only while a replacement is genuinely expected, so
            a stale marker cannot pull focus out of nowhere at some later swap.
          */
          const replacing = this.applying || this.classList.contains('ajax-loading');
          this.pendingRehome = replacing
            ? { mobile, wantsSort: held.closest('.lunya-plp-dropdown--sort, .lunya-plp-mobile-bar__sort') != null }
            : null;
          window.clearTimeout(this.rehomeTimer);
          let tries = 0;
          const attempt = () => {
            if (settle() || tries >= 3) return;
            tries += 1;
            this.rehomeTimer = window.setTimeout(attempt, REHOME_SETTLE);
          };
          window.requestAnimationFrame(() => window.requestAnimationFrame(attempt));
        }
        if (!mobile && this.pendingToggleFocus) this.pendingToggleFocus = false;
      };
      this.boundChangeCapture = this.onChangeCapture.bind(this);
      this.boundDocumentClick = this.onDocumentClick.bind(this);
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundFocusIn = this.onFocusIn.bind(this);
      this.boundPopState = this.onPopState.bind(this);
      this.boundBarPosition = this.scheduleBarPosition.bind(this);

      this.addEventListener('click', this.boundClick);
      // Capture phase so the panel's ticks can be stopped before Symmetry's delegated form handler.
      this.addEventListener('change', this.boundChangeCapture, true);
      /*
        Capture, not bubble: the mobile navigation drawer installs the theme's singleton trap
        synchronously from its own click handler (assets/main.js, the `.mobile-nav-toggle` click delegate). On bubble this listener runs
        after that, and closing the filter panel would release the drawer's newly installed trap.
        Capture puts our close first, leaving the incoming overlay's trap intact.
      */
      document.addEventListener('click', this.boundDocumentClick, true);
      document.addEventListener('keydown', this.boundKeydown);
      /*
        The pending focus id has to follow the shopper, not the control they ticked: Symmetry saves
        `document.activeElement.id` when the response lands (assets/main.js, FilterContainer.ajaxLoadUrl, "save active element"), which is 700ms of
        debounce plus a fetch after the change, so a keyboard shopper can be several controls along
        by then. Tracking focusin keeps the two in agreement instead of racing them.
      */
      document.addEventListener('focusin', this.boundFocusIn);
      // Symmetry binds its own popstate under the same condition (assets/main.js, FilterContainer.connectedCallback, "back button").
      window.addEventListener('popstate', this.boundPopState);
      window.addEventListener('scroll', this.boundBarPosition, { passive: true });
      window.addEventListener('resize', this.boundBarPosition);
      onMediaChange(MOBILE, this.boundBreakpoint);
      // Header resizing, delayed product images and Load More can change the
      // pinning boundaries without a window resize. Observe the stable shells;
      // native filtering replaces their children rather than these elements.
      if (window.ResizeObserver) {
        this.barResizeObserver = new ResizeObserver(this.boundBarPosition);
        [this, this.closest('filter-container')?.querySelector('.collection-listing'),
          document.querySelector('.section-header')].filter(Boolean)
          .forEach(el => this.barResizeObserver.observe(el));
      }
      // Native PageHeader publishes this root CSS offset after its own 300ms
      // resize debounce. Geometry observation alone would keep the old offset
      // until the next scroll. Attribute observation also handles sticky on/off.
      this.headerOffsetObserver = new MutationObserver(this.boundBarPosition);
      this.headerOffsetObserver.observe(document.documentElement, {
        attributes: true, attributeFilter: ['style']
      });

      // Symmetry swaps innerHTML after every ajax load; restore the dropdown the shopper had open.
      this.observer = new MutationObserver(() => {
        if (this.openHandle) {
          this.openDropdown(this.openHandle);
          this.restorePendingFocus();
        }
        if (document.documentElement.classList.contains(OPEN_CLASS)) this.closeMobile();
        // The response landed, so the submitted state is captured and the panel is safe to reopen.
        this.releaseApplyLock();
        this.restoreToggleFocus();
        /*
          A crossing that was still re-homing when this swap arrived: the node it was aiming at is
          gone. Only act when nothing else claimed focus, so the two restores above keep priority.
        */
        if (this.pendingRehome) {
          const { mobile: wasMobile, wantsSort, id: survivorId } = this.pendingRehome;
          this.pendingRehome = null;
          if (!document.activeElement || document.activeElement === document.body) {
            const survivor = survivorId ? this.querySelector(`#${CSS.escape(survivorId)}`) : null;
            const target = survivor || this.counterpartIn(wasMobile, wantsSort);
            if (target) {
              void target.offsetWidth;
              target.focus();
            }
          }
        }
        // Last, so it wins over the aria-expanded openDropdown may have just set.
        this.syncTriggerSemantics();
        this.scheduleBarPosition();
      });
      this.observer.observe(this, { childList: true });
      this.syncTriggerSemantics();
      this.scheduleBarPosition();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      // The capture flag has to match the addEventListener call or the listener is not removed.
      this.removeEventListener('change', this.boundChangeCapture, true);
      document.removeEventListener('click', this.boundDocumentClick, true);
      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('focusin', this.boundFocusIn);
      window.removeEventListener('popstate', this.boundPopState);
      offMediaChange(MOBILE, this.boundBreakpoint);
      window.clearTimeout(this.applyTimer);
      window.clearTimeout(this.rehomeTimer);
      this.cancelQueuedSubmit();
      window.removeEventListener('scroll', this.boundBarPosition);
      window.removeEventListener('resize', this.boundBarPosition);
      window.cancelAnimationFrame(this.barPositionFrame);
      this.barPositionFrame = null;
      if (this.barResizeObserver) this.barResizeObserver.disconnect();
      if (this.headerOffsetObserver) this.headerOffsetObserver.disconnect();
      this.classList.remove('lunya-plp-toolbar--pinned');
      this.style.removeProperty('--lunya-plp-bar-top');
      // The scroll lock and the panel offset live on `html` and on this element's style attribute,
      // so a section reload with the panel open would leave the page locked after this toolbar goes.
      this.closeMobile();
      if (this.observer) this.observer.disconnect();
      this.wired = false;
    }

    get form() {
      return this.querySelector('#CollectionFilterForm');
    }

    get panelInputs() {
      return Array.from(this.querySelectorAll('.lunya-plp-toolbar__groups .lunya-plp-option__input'));
    }

    isMobile() {
      return MOBILE.matches;
    }

    scheduleBarPosition() {
      if (this.barPositionFrame) return;
      this.barPositionFrame = window.requestAnimationFrame(() => {
        this.barPositionFrame = null;
        this.updateBarPosition();
      });
    }

    updateBarPosition() {
      const bar = this.querySelector('.lunya-plp-mobile-bar');
      const anchor = this.querySelector('.lunya-plp-mobile-bar-anchor');
      const listing = this.closest('filter-container')?.querySelector('.collection-listing');
      if (!this.isMobile() || !bar || !anchor || !listing) {
        this.classList.remove('lunya-plp-toolbar--pinned');
        this.style.removeProperty('--lunya-plp-bar-top');
        return;
      }
      const offset = Math.max(0, (parseFloat(getComputedStyle(document.documentElement)
        .getPropertyValue('--theme-sticky-header-height')) || 0) - 1);
      const end = listing.getBoundingClientRect().bottom;
      const pinned = anchor.getBoundingClientRect().top <= offset && end > offset;
      // Push out at the listing boundary rather than following into FAQs/footer.
      // Once the listing has passed, restore normal flow: a permanently fixed,
      // offscreen control cannot be scrolled into view by keyboard focus.
      // Do not clone or move controls: native serialization and focus keep the
      // original form, IDs, and the delegated handlers across every AJAX swap.
      this.classList.toggle('lunya-plp-toolbar--pinned', pinned);
      if (pinned) this.style.setProperty('--lunya-plp-bar-top', `${Math.min(offset, end - bar.offsetHeight)}px`);
      else this.style.removeProperty('--lunya-plp-bar-top');
      if (document.documentElement.classList.contains(OPEN_CLASS)) {
        this.style.setProperty('--lunya-plp-panel-top', `${Math.round(bar.getBoundingClientRect().bottom)}px`);
      }
    }

    positionMobileBarForUse(park = false) {
      if (!this.isMobile()) return;
      const bar = this.querySelector('.lunya-plp-mobile-bar');
      const anchor = this.querySelector('.lunya-plp-mobile-bar-anchor');
      if (!bar || !anchor) return;
      const offset = Math.max(0, (parseFloat(getComputedStyle(document.documentElement)
        .getPropertyValue('--theme-sticky-header-height')) || 0) - 1);
      const rect = bar.getBoundingClientRect();
      if (rect.top < offset || rect.bottom > window.innerHeight
        || (park && !this.classList.contains('lunya-plp-toolbar--pinned'))) {
        // Use the in-flow anchor, not the fixed/pushed-out bar's viewport rect.
        // Restore clipped focus before opening the scroll-locked panel too.
        window.scrollTo({ top: anchor.getBoundingClientRect().top + window.scrollY - offset, behavior: 'auto' });
      }
      this.updateBarPosition();
    }

    /*
      On mobile the group triggers are static headings: CSS gives them `pointer-events: none` and
      onClick ignores them (`!this.isMobile()`). A <button> stays in the tab order regardless, so a
      keyboard shopper hit seven stops that did nothing and were announced as collapsed buttons.
      Take them out of the tab order at that width and drop the `aria-expanded` that no longer
      describes them; restore both on desktop. Re-run after each ajax swap and on a breakpoint
      change, since the markup is replaced and the viewport can cross 900px at any time.
    */
    syncTriggerSemantics() {
      const mobile = this.isMobile();
      this.querySelectorAll('.lunya-plp-dropdown__trigger').forEach((trigger) => {
        if (mobile) {
          trigger.setAttribute('tabindex', '-1');
          // `tabindex` alone leaves it a button in the accessibility tree, which browse mode still
          // announces as actionable; at this width it is a static group heading, so say so.
          trigger.setAttribute('role', 'heading');
          trigger.setAttribute('aria-level', '2');
          trigger.removeAttribute('aria-expanded');
          return;
        }
        trigger.removeAttribute('tabindex');
        trigger.removeAttribute('role');
        trigger.removeAttribute('aria-level');
        const dropdown = trigger.closest('.lunya-plp-dropdown');
        trigger.setAttribute('aria-expanded', dropdown && dropdown.classList.contains('is-open') ? 'true' : 'false');
      });
    }

    /*
      Symmetry only registers a `submit` handler on #CollectionFilterForm when ajax filtering is on
      (assets/main.js: `if (this.dataset.ajaxFiltering === 'true')`); with `ajax_products` off it
      registers a `change` handler that calls form.submit() instead. Apply therefore has to pick its
      path. With no filter-container at all there is no Symmetry handler either, so treat that as
      non-ajax and submit natively.
    */
    ajaxFiltering() {
      const container = this.closest('filter-container');
      return !!container && container.dataset.ajaxFiltering === 'true';
    }

    /* Desktop dropdowns */

    openDropdown(handle) {
      this.closeDropdowns();
      const dropdown = this.querySelector(`.lunya-plp-dropdown[data-lunya-dropdown="${CSS.escape(handle)}"]`);
      if (!dropdown) return;
      dropdown.classList.add('is-open');
      const trigger = dropdown.querySelector('.lunya-plp-dropdown__trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.openHandle = handle;
    }

    /*
      `returnFocus` is for deliberate dismissal (Escape, the CLOSE control): closing makes the panel
      `visibility: hidden`, so a focused option inside it stops being focusable and the browser drops
      focus to the body. Not used when another dropdown is opening or on an outside click, where
      focus belongs wherever the shopper just put it.
    */
    closeDropdowns(returnFocus = false) {
      this.querySelectorAll('.lunya-plp-dropdown.is-open').forEach((dropdown) => {
        const trigger = dropdown.querySelector('.lunya-plp-dropdown__trigger');
        const heldFocus = returnFocus && dropdown.contains(document.activeElement);
        dropdown.classList.remove('is-open');
        if (!trigger) return;
        trigger.setAttribute('aria-expanded', 'false');
        if (heldFocus) trigger.focus();
      });
      this.openHandle = null;
    }

    /* Mobile panel */

    openMobile() {
      this.snapshot = this.panelInputs.map((input) => input.checked);
      // As the source does (`scrollTo('#toolbar-mobile-trigger')`): park the bar under the sticky
      // header, then hang the panel off the bar's real bottom edge so it lines up even when the page
      // is too short to scroll that far.
      const bar = this.querySelector('.lunya-plp-mobile-bar');
      this.positionMobileBarForUse(true);
      if (bar) this.style.setProperty('--lunya-plp-panel-top', `${Math.round(bar.getBoundingClientRect().bottom)}px`);
      document.documentElement.classList.add(OPEN_CLASS);
      this.setToggleExpanded(true);
      this.updateApplyLabel();
      /*
        The panel is fixed, opaque and full height with the page scroll locked, so Tab must not walk
        past Cancel into the product links behind it. Trapping on the form covers the bar's toggle
        and sort select as well as the panel's options and buttons, and excludes the applied chips
        and the listing. The trap only counts visible controls, so the closed panel is never in it.
      */
      const form = this.form;
      if (form) trapFocusIn(form, this.querySelector('[data-lunya-mobile-toggle]'));
    }

    /*
      `returnFocus` mirrors closeDropdowns: the panel becomes `visibility: hidden` on close, so a
      focused control inside it stops being focusable and focus falls to the document. Cancel,
      Escape and the toggle itself pass it; the disconnect and post-render paths do not.
    */
    closeMobile(returnFocus = false) {
      const wasOpen = document.documentElement.classList.contains(OPEN_CLASS);
      const panel = this.querySelector('.lunya-plp-toolbar__groups');
      const toggle = this.querySelector('[data-lunya-mobile-toggle]');
      const heldFocus = returnFocus && panel != null && panel.contains(document.activeElement);
      document.documentElement.classList.remove(OPEN_CLASS);
      this.style.removeProperty('--lunya-plp-panel-top');
      this.setToggleExpanded(false);
      this.snapshot = null;
      /*
        Release on every close path, including disconnect — but only a trap this panel set: the
        theme keeps one set of trap handlers, so releasing unconditionally would drop the cart
        drawer's or the search overlay's trap instead.
      */
      if (wasOpen) releaseFocusTrap(heldFocus ? toggle : null);
      else if (heldFocus && toggle) toggle.focus();
    }

    /* Apply re-renders the toolbar, so the toggle to focus is the replacement, not this one. */
    restoreToggleFocus() {
      if (!this.pendingToggleFocus) return;
      this.pendingToggleFocus = false;
      const toggle = this.querySelector('[data-lunya-mobile-toggle]');
      if (toggle) toggle.focus();
    }

    setToggleExpanded(expanded) {
      const toggle = this.querySelector('[data-lunya-mobile-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    pendingCount() {
      if (!this.snapshot) return 0;
      return this.panelInputs.reduce((count, input, index) => count + (input.checked !== this.snapshot[index] ? 1 : 0), 0);
    }

    updateApplyLabel() {
      const apply = this.querySelector('[data-lunya-apply]');
      if (!apply) return;
      const count = this.pendingCount();
      apply.textContent = count > 0
        ? apply.dataset.lunyaApplyCountLabel.replace('__count__', String(count))
        : apply.dataset.lunyaApplyLabel;
    }

    /*
      Symmetry's ajax pass saves `document.activeElement.id` and calls `focus()` on the replacement
      after the swap — but its restore runs synchronously, before the observer above reopens the
      dropdown, and a checkbox inside a closed (visibility: hidden) panel cannot take focus. So the
      ids alone are not enough: re-apply focus once the dropdown is open again.
    */
    restorePendingFocus() {
      const id = this.pendingFocusId;
      this.pendingFocusId = null;
      if (!id) return;
      const focusById = () => {
        const el = this.querySelector(`#${CSS.escape(id)}`);
        if (!el) return true;
        // Reading a layout property flushes the class openDropdown just added; without it the
        // browser still sees the panel as `visibility: hidden` and focus() is a silent no-op.
        void el.offsetWidth;
        el.focus();
        return document.activeElement === el;
      };
      if (!focusById()) requestAnimationFrame(focusById);
    }

    /*
      Each width renders one of a pair and hides the other: below 900px the bar's native select and
      FILTER toggle, above it the sort dropdown's trigger and the group triggers. Hand the shopper
      the counterpart of whatever they were using, and fall back to the other half, since a
      collection with no renderable groups renders the sort half alone and no toggle at all.
    */
    /*
      Whichever half of the pair the given width renders, falling back to the other: a collection
      with no renderable groups shows the bar's sort alone and no FILTER toggle, and a store with
      sorting off shows no sort control at all. Shared with the post-swap one-shot, which has only
      the intent to go on because the node it was aiming at is gone by then.
    */
    counterpartIn(mobile, wantsSort) {
      if (mobile) {
        const select = this.querySelector('.lunya-plp-mobile-sort');
        const toggle = this.querySelector('[data-lunya-mobile-toggle]');
        return (wantsSort ? select : toggle) || select || toggle;
      }
      const sortTrigger = this.querySelector('.lunya-plp-dropdown--sort .lunya-plp-dropdown__trigger');
      const groupTrigger = this.querySelector('.lunya-plp-dropdown:not(.lunya-plp-dropdown--sort) .lunya-plp-dropdown__trigger');
      return (wantsSort ? sortTrigger : groupTrigger) || groupTrigger || sortTrigger;
    }

    /*
      The chip next along keeps its id through the swap, because its own filter stays active — so it
      is the closest thing to "where the shopper was". Clear sits outside the list and the last chip
      has no sibling, and in both cases the applied row goes away entirely, so there is nothing to
      return and the caller falls back to a control the width actually renders.
    */
    survivingAppliedId(link) {
      const item = link.closest('li');
      const sibling = item && (item.nextElementSibling || item.previousElementSibling);
      const chip = sibling && sibling.querySelector('.lunya-plp-chip');
      return chip && chip.id ? chip.id : null;
    }

    counterpartFor(held, mobile) {
      const wantsSort = held.closest('.lunya-plp-dropdown--sort, .lunya-plp-mobile-bar__sort') != null;
      // Above 900px, prefer the trigger of the group the shopper was actually in.
      if (!mobile && !wantsSort) {
        const own = held.closest('.lunya-plp-dropdown');
        const ownTrigger = own && own.querySelector('.lunya-plp-dropdown__trigger');
        if (ownTrigger) return ownTrigger;
      }
      return this.counterpartIn(mobile, wantsSort);
    }

    /*
      Apply queues Symmetry's debounced handler, which serialises the live form 700ms later
      (assets/main.js, FilterContainer.ajaxLoadForm) — so a tick made in that window would ride along on a request the
      shopper never applied it to, which is exactly the batching the panel promises not to do. The
      panel stays shut until the response lands. The timer is a dead-man's switch: a failed request
      produces no mutation, and without it the toggle would stay inert for the life of the page.
    */
    takeApplyLock() {
      this.applying = true;
      const toggle = this.querySelector('[data-lunya-mobile-toggle]');
      if (toggle) toggle.setAttribute('aria-disabled', 'true');
      window.clearTimeout(this.applyTimer);
      this.applyTimer = window.setTimeout(() => this.onApplyLockTimeout(), APPLY_LOCK_MAX);
    }

    /*
      A request still outstanding this long is one this element has given up on, but Symmetry sets
      no timeout of its own, so it can still land and replace the toolbar. Releasing alone would
      invite edits underneath a response that is still authoritative; abort it first, the way
      Symmetry aborts a superseded one (assets/main.js, FilterContainer.ajaxLoadUrl, "cancel current fetch").
    */
    onApplyLockTimeout() {
      const container = this.closest('filter-container');
      const controller = container && container.ajaxLoadUrlFetchAbortController;
      if (controller && typeof controller.abort === 'function') {
        controller.abort('Lunya PLP: filter request timed out');
        /*
          Symmetry drops the loading class on the success path alone (assets/main.js, FilterContainer.ajaxLoadUrl, "remove loading state") — its
          catch only logs — so an abort would leave every container dimmed for good.
        */
        const section = this.closest('.shopify-section');
        if (section) {
          section.querySelectorAll('[data-ajax-container]').forEach((el) => {
            el.classList.remove('ajax-loading');
          });
        }
        /*
          `ajaxLoadUrl` pushes the requested URL before it fetches (assets/main.js, FilterContainer.ajaxLoadUrl, "update url history"), so the
          address bar already describes filters the aborted response was going to render. Unlocking
          here would leave an interactive page whose URL and grid disagree, with nothing to show
          the shopper it had failed. Ten seconds in, the ajax attempt is spent: let the browser
          fetch the URL that is already in the bar, which is the state the shopper asked for.
        */
        window.location.reload();
        return;
      }
      // No swap will arrive to consume it, so the crossing's one-shot has to go with the request.
      this.pendingRehome = null;
      this.releaseApplyLock();
    }

    releaseApplyLock() {
      if (!this.applying) return;
      this.applying = false;
      window.clearTimeout(this.applyTimer);
      const toggle = this.querySelector('[data-lunya-mobile-toggle]');
      if (toggle) toggle.removeAttribute('aria-disabled');
    }

    /*
      Symmetry debounces its handler by 700ms, which is right for a run of desktop ticks and wrong
      for a deliberate one-shot commit: in the gap between the act and the serialisation, any
      response already in flight replaces this toolbar, and the delayed pass then serialises the
      replacement — losing the shopper's selections with nothing to show for it. Three review rounds
      found a different way into that gap. Calling the controller directly closes it for both
      one-shot paths: it serialises the DOM the shopper acted on, now, and `ajaxLoadUrl` aborts
      whatever was in flight. Falls back to the queued path if the method ever moves.
    */
    canSubmitNow() {
      const container = this.closest('filter-container');
      return !!container && typeof container.ajaxLoadForm === 'function';
    }

    submitNow(evt) {
      if (!this.canSubmitNow()) return false;
      this.cancelQueuedSubmit();
      this.closest('filter-container').ajaxLoadForm(evt || { type: 'lunya-apply' });
      return true;
    }

    /*
      The desktop debounce is run here rather than left to Symmetry's, which is created as a local
      inside its connectedCallback and so cannot be cancelled from outside. A queued call that
      cannot be cancelled is the problem: tick a checkbox, then choose a sort or drop a chip inside
      the window, and the queued pass serialises the pre-click form — sort links are not inputs, so
      they do not survive that serialisation — and aborts the newer request on its way out, undoing
      the click. Same interval, same behaviour, one clock this element can stop.
    */
    queueSubmit() {
      window.clearTimeout(this.submitTimer);
      this.submitTimer = window.setTimeout(() => {
        this.submitTimer = null;
        this.submitNow();
      }, SUBMIT_DEBOUNCE);
    }

    cancelQueuedSubmit() {
      window.clearTimeout(this.submitTimer);
      this.submitTimer = null;
    }

    restoreSnapshot() {
      if (!this.snapshot) return;
      this.panelInputs.forEach((input, index) => {
        input.checked = this.snapshot[index];
      });
    }

    /* Events */

    onClick(evt) {
      const trigger = evt.target.closest('.lunya-plp-dropdown__trigger');
      if (trigger && !this.isMobile()) {
        const dropdown = trigger.closest('.lunya-plp-dropdown');
        const handle = dropdown.dataset.lunyaDropdown;
        if (dropdown.classList.contains('is-open')) {
          this.closeDropdowns();
        } else {
          this.openDropdown(handle);
        }
        return;
      }

      if (evt.target.closest('[data-lunya-close]')) {
        this.closeDropdowns(true);
        return;
      }

      if (evt.target.closest('.lunya-plp-sort__link')) {
        /*
          Symmetry intercepts these links itself and loads straight away (assets/main.js, FilterContainer constructor, the `ajaxFiltering` link delegate), so
          nothing here may preventDefault — this listener only has to leave the menu in a sane state
          first. Left open, `openHandle` would have the observer reopen it after the swap, over a
          link the swap has removed; the trigger carries a stable id, so handing focus there lets
          Symmetry's own restore put it back on the replacement.
        */
        this.closeDropdowns(true);
        return;
      }

      if (evt.target.closest('[data-lunya-mobile-toggle]')) {
        // Reopening mid-Apply would let a fresh tick join the request that is already on its way.
        if (this.applying) return;
        if (document.documentElement.classList.contains(OPEN_CLASS)) {
          this.restoreSnapshot();
          this.closeMobile(true);
        } else {
          this.openMobile();
        }
        return;
      }

      if (evt.target.closest('[data-lunya-cancel]')) {
        this.restoreSnapshot();
        this.closeMobile(true);
        return;
      }

      if (evt.target.closest('[data-lunya-apply]')) {
        const form = this.form;
        // The ajax pass replaces this toggle, so the focus has to be re-applied after the swap.
        this.pendingToggleFocus = document.activeElement != null && this.contains(document.activeElement);
        if (form && this.ajaxFiltering()) {
          // A synthetic submit event reaches Symmetry's (debounced) submit handler, which runs
          // ajaxLoadForm and re-renders this element, without ever starting a native submission —
          // a real form submission would, because Symmetry's preventDefault runs 700ms late. The
          // observer closes the panel after the re-render.
          this.takeApplyLock();
          if (!this.submitNow()) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        } else if (form) {
          // No submit handler exists in non-ajax mode, and the panel's ticks never reached the
          // change handler either, so nothing but a real submission applies them.
          form.submit();
        }
        /*
          Full close, not just the class: the toggle's aria-expanded and the inline panel offset
          would otherwise keep claiming the panel is open. Returning focus is not deferred to the
          restore above — submission is debounced 700ms and then waits on the network, and focus
          would sit on the hidden Apply button for all of it. Hand it to the visible toggle now;
          `pendingToggleFocus` re-applies it to the replacement once the swap lands.
        */
        this.closeMobile(true);
      }
    }

    onChangeCapture(evt) {
      if (evt.target.classList && evt.target.classList.contains('lunya-plp-mobile-sort')) {
        /*
          This change goes on to Symmetry's debounced handler, which serialises the live form 700ms
          later — the same window Apply locks. Committing ticks already made is deliberate and
          matches the source, whose sort change closes the panel and then reads the checked boxes;
          what must not happen is a tick made after this point joining the same request. The panel
          closes without restoring the snapshot, so those earlier ticks still commit.
        */
        if (this.ajaxFiltering()) {
          this.takeApplyLock();
          // Loaded here, so the delegated handler must not queue the same request again 700ms on.
          if (this.submitNow(evt)) evt.stopPropagation();
        }
        if (document.documentElement.classList.contains(OPEN_CLASS)) this.closeMobile();
        return;
      }
      if (!evt.target.classList || !evt.target.classList.contains('lunya-plp-option__input')) return;
      this.pendingFocusId = evt.target.id || null;
      if (this.isMobile() && document.documentElement.classList.contains(OPEN_CLASS)) {
        // Batch: keep Symmetry from ajax-loading on every tick; Apply submits the form instead.
        evt.stopPropagation();
        this.updateApplyLabel();
        return;
      }
      /*
        Otherwise this tick reaches Symmetry's debounced handler and a replacement is on its way —
        the last path into the window the links and the sort already close. It keeps the debounce,
        which is what batches a run of desktop ticks, so the lock does the guarding: rotate to
        mobile before it lands and a panel opened in between would fold its ticks into that request
        with no Apply, or lose them to the response.
      */
      if (!this.ajaxFiltering()) return;
      this.takeApplyLock();
      // Without the controller there is nothing to drive, so leave the event to Symmetry's own
      // debounce and keep today's behaviour rather than swallowing the tick entirely.
      if (!this.canSubmitNow()) return;
      evt.stopPropagation();
      /*
        With a fetch already live the wait is the danger, not the batching: that response replaces
        the form before any delayed pass runs, and the pass then serialises the replacement without
        this tick in it. Serialise now instead — `ajaxLoadUrl` aborts the live fetch on its way
        through. Symmetry marks that window with `ajax-loading` on every `[data-ajax-container]`
        (assets/main.js, FilterContainer.ajaxLoadUrl, "loading state"), this element included, so a quiet run of ticks still batches.
      */
      if (this.classList.contains('ajax-loading')) this.submitNow(evt);
      else this.queueSubmit();
    }

    /*
      Keeps the pending target honest between the change and the swap. Focus moving to another
      option inside this toolbar re-aims the restore at that option — Symmetry cannot restore it
      itself, because the replacement dropdown renders closed and a hidden control refuses focus.
      Focus leaving the toolbar clears the restore entirely, so Symmetry's own pass is left to place
      it and this element never drags a shopper back to a control they have moved on from.

      Only elements carrying an id are tracked, since an id is what both restore paths key on.
      Removing the focused node during the swap does not fire focusin, so the pending id set by the
      shopper's own change survives the replacement.
    */
    onFocusIn(evt) {
      const el = evt.target;
      const inside = this.contains(el);
      if (inside && el.closest('.lunya-plp-mobile-bar')) this.positionMobileBarForUse();
      if (inside) this.lastInsideFocus = { el, at: now() };
      /*
        Arming on focus, not only on a change: a replacement started elsewhere — a chip, Clear,
        pagination — will remove this control too, and Symmetry's own restore cannot reach anything
        inside a dropdown because the replacement renders it closed and a hidden control refuses
        focus. Anything identified inside a dropdown is therefore worth remembering; the reopen is
        what makes it focusable again.
      */
      if (inside && el.id && el.closest('.lunya-plp-dropdown')) this.pendingFocusId = el.id;
      else if (this.pendingFocusId) this.pendingFocusId = inside && el.id ? el.id : null;
      /*
        The Apply restore races the same debounce and fetch: focus that has moved to the header,
        the cart drawer or the listing outranks a toggle this element is only replacing. Focus
        falling to `body` because the swap removed the focused Apply button fires no focusin, so
        that case still restores.
      */
      if (this.pendingToggleFocus && el.closest('[data-lunya-mobile-toggle]') == null) {
        this.pendingToggleFocus = false;
      }
    }

    /*
      Back and Forward start a replacement with no click to intercept (assets/main.js, FilterContainer.ajaxPopState), so the
      lock has to be taken here too, or a panel opened before that response lands loses its ticks to
      it. An open panel is discarded as Cancel does: the navigation is the authoritative state.
    */
    onPopState() {
      if (!this.ajaxFiltering()) return;
      this.cancelQueuedSubmit();
      this.takeApplyLock();
      if (document.documentElement.classList.contains(OPEN_CLASS)) {
        this.restoreSnapshot();
        this.closeMobile(true);
      }
    }

    onDocumentClick(evt) {
      /*
        The header stays above the panel (`.section-header` is z-index 400, this panel 90), so the
        shopper can open search or the cart drawer with the filters still open. The theme's trap is a
        singleton the search overlay takes over, and closing that overlay releases it without
        restoring ours — leaving an opaque, scroll-locked panel that Tab can escape. Two modal
        surfaces should not share the screen, so a click anywhere above this toolbar closes ours,
        discarding pending edits as Cancel does. Focus follows whatever the shopper clicked.
      */
      if (document.documentElement.classList.contains(OPEN_CLASS) && !this.contains(evt.target)) {
        this.restoreSnapshot();
        this.closeMobile();
      }

      /*
        Symmetry loads each of these the moment it is clicked (assets/main.js, FilterContainer constructor, the `ajaxFiltering` link delegate) and the response
        replaces this toolbar. Apply only queues its submission 700ms out, so a panel opened in
        between would have its ticks wiped by that replacement and the queued Apply would then
        serialise the replacement instead — the shopper's selections lost with no sign of it. The
        lock the sort change already takes covers the rest: hold the panel shut until this lands.
      */
      const ajaxLink = evt.target.closest(AJAX_LINKS);
      const section = this.closest('.shopify-section');
      if (ajaxLink && section && section.contains(ajaxLink) && this.ajaxFiltering()) {
        // Symmetry loads this now; a queued tick would serialise the pre-click form afterwards and
        // abort it, undoing the click.
        this.cancelQueuedSubmit();
        this.takeApplyLock();
        /*
          A chip or Clear is the one control the replacement cannot contain — removing the filter is
          the point — so Symmetry records an id that will not be there and focus falls to the
          document. Measured on stage: all three of a chip with others applied, the last chip, and
          Clear left focus on `body`. Aim at something that survives, before the request starts.
        */
        if (this.contains(ajaxLink) && ajaxLink.closest('.lunya-plp-applied')) {
          this.pendingRehome = {
            mobile: this.isMobile(),
            wantsSort: false,
            id: this.survivingAppliedId(ajaxLink),
          };
        }
      }

      if (!this.openHandle) return;
      if (this.contains(evt.target) && evt.target.closest('.lunya-plp-dropdown')) return;
      this.closeDropdowns();
    }

    onKeydown(evt) {
      if (evt.key !== 'Escape') return;
      if (this.openHandle) this.closeDropdowns(true);
      if (document.documentElement.classList.contains(OPEN_CLASS)) {
        this.restoreSnapshot();
        this.closeMobile(true);
      }
    }
  }

  window.customElements.define('lunya-plp-toolbar', LunyaPlpToolbar);
})();
